import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  TextField,
  Divider,
  IconButton,
  LinearProgress,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  PictureAsPdf as PdfIcon,
  SmartToy as AIIcon,
} from '@mui/icons-material';
import { AppDispatch, RootState } from '../../store/store';
import { processInvoiceWithAI, createInvoiceFromAI, clearAIProcessing } from '../../store/slices/invoiceSlice';
import { InvoiceAIData, InvoiceAIItem } from '../../types/aiInvoice';

interface AIInvoiceUploadProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AIInvoiceUpload: React.FC<AIInvoiceUploadProps> = ({ open, onClose, onSuccess }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { aiProcessing } = useSelector((state: RootState) => state.invoices);

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [editedData, setEditedData] = useState<InvoiceAIData | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [processingTime, setProcessingTime] = useState(0);

  const steps = ['Nahrát PDF', 'AI Zpracování', 'Kontrola Dat', 'Vytvořit Fakturu'];

  // Update edited data when extracted data changes
  useEffect(() => {
    if (aiProcessing.extractedData) {
      setEditedData({ ...aiProcessing.extractedData });
      setActiveStep(2); // Move to review step
    }
  }, [aiProcessing.extractedData]);

  // Handle file selection
  const handleFileSelect = (file: File | null) => {
    if (file && file.type === 'application/pdf') {
      setUploadedFile(file);
      setActiveStep(0);
      
      // Create preview URL for PDF
      const url = URL.createObjectURL(file);
      setPdfPreviewUrl(url);
    } else if (file) {
      alert('Prosím vyberte PDF soubor');
    }
  };
  
  // Cleanup preview URL when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
    };
  }, [pdfPreviewUrl]);

  // Handle drag events
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  // Timer for processing time display
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (aiProcessing.isProcessing && activeStep === 1) {
      setProcessingTime(0);
      interval = setInterval(() => {
        setProcessingTime((prev) => prev + 1);
      }, 1000);
    } else {
      setProcessingTime(0);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [aiProcessing.isProcessing, activeStep]);

  // Start AI processing
  const handleStartProcessing = async () => {
    if (!uploadedFile) return;

    setActiveStep(1);
    setProcessingTime(0);
    try {
      await dispatch(processInvoiceWithAI(uploadedFile)).unwrap();
      // Step will be updated automatically by useEffect when extractedData changes
    } catch (error) {
      console.error('AI Processing failed:', error);
      setActiveStep(0);
    }
  };

  // Handle field changes
  const handleFieldChange = (field: keyof InvoiceAIData, value: any) => {
    if (editedData) {
      setEditedData({
        ...editedData,
        [field]: value,
      });
    }
  };

  // Handle item changes
  const handleItemChange = (index: number, field: keyof InvoiceAIItem, value: any) => {
    if (editedData && editedData.items) {
      const newItems = [...editedData.items];
      newItems[index] = {
        ...newItems[index],
        [field]: value,
      };
      setEditedData({
        ...editedData,
        items: newItems,
      });
    }
  };

  // Create invoice
  const handleCreateInvoice = async () => {
    if (!editedData || !uploadedFile) return;

    setActiveStep(3);
    try {
      await dispatch(createInvoiceFromAI({ invoiceData: editedData, pdfFile: uploadedFile })).unwrap();
      
      // Success
      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    } catch (error) {
      console.error('Invoice creation failed:', error);
      setActiveStep(2);
    }
  };

  // Close and reset
  const handleClose = () => {
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
    }
    setUploadedFile(null);
    setEditedData(null);
    setActiveStep(0);
    setPdfPreviewUrl(null);
    dispatch(clearAIProcessing());
    onClose();
  };

  // Render upload area
  const renderUploadArea = () => (
    <Box>
      <Paper
        sx={{
          p: 4,
          border: dragActive ? '2px dashed #1976d2' : '2px dashed #ccc',
          backgroundColor: dragActive ? '#f0f7ff' : '#fafafa',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s',
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => document.getElementById('pdf-file-input')?.click()}
      >
        <UploadIcon sx={{ fontSize: 64, color: '#1976d2', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Nahrát Fakturu PDF
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Přetáhněte PDF soubor sem, nebo klikněte pro výběr
        </Typography>
        <input
          id="pdf-file-input"
          type="file"
          accept="application/pdf"
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
        />
      </Paper>

      {uploadedFile && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="success" icon={<PdfIcon />}>
            <Typography variant="body2">
              <strong>Soubor vybrán:</strong> {uploadedFile.name} ({(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)
            </Typography>
          </Alert>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<AIIcon />}
              onClick={handleStartProcessing}
            >
              Zpracovat s AI
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );

  // Render processing status
  const renderProcessingStatus = () => {
    // Calculate estimated progress based on time (simulated)
    const estimatedProgress = Math.min(95, Math.floor((processingTime / 15) * 100));
    
    // Generate status message based on elapsed time
    const getStatusMessage = () => {
      if (processingTime < 2) return '⏳ Nahrávám PDF na server...';
      if (processingTime < 4) return '🤖 Spouštím AI asistenta...';
      if (processingTime < 8) return '📄 AI čte a analyzuje fakturu...';
      if (processingTime < 12) return '🔍 AI extrahuje data z dokumentu...';
      if (processingTime < 15) return '✅ AI validuje extrahovaná data...';
      return '⏳ Dokončuji zpracování...';
    };

    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress size={80} sx={{ mb: 3 }} />
        <Typography variant="h6" gutterBottom>
          AI Zpracovává Vaši Fakturu...
        </Typography>
        <Typography variant="body2" color="primary" sx={{ mb: 2 }}>
          {getStatusMessage()}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Čas zpracování: {processingTime}s
        </Typography>
        
        {/* Show status history if available */}
        {aiProcessing.statusHistory.length > 0 ? (
          <Box sx={{ mt: 3, px: 2 }}>
            <Paper elevation={2} sx={{ p: 2, backgroundColor: 'grey.50', maxHeight: 300, overflowY: 'auto' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Detailní průběh:
              </Typography>
              {aiProcessing.statusHistory.map((status, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    mb: 1.5,
                    pb: 1.5,
                    borderBottom: index < aiProcessing.statusHistory.length - 1 ? '1px solid #e0e0e0' : 'none'
                  }}
                >
                  <Box sx={{ mr: 1, fontSize: '1rem' }}>
                    {status.status === 'completed' ? '✅' : 
                     status.status === 'error' ? '❌' : 
                     status.status === 'in_progress' ? '⏳' : '⏸️'}
                  </Box>
                  <Box sx={{ flex: 1, textAlign: 'left' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: status.status === 'in_progress' ? 600 : 400,
                        color: status.status === 'completed' ? 'success.main' : 
                               status.status === 'error' ? 'error.main' : 
                               status.status === 'in_progress' ? 'primary.main' : 'text.secondary'
                      }}
                    >
                      {status.message}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {new Date(status.timestamp).toLocaleTimeString('cs-CZ')}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Paper>
          </Box>
        ) : (
          <Alert severity="info" sx={{ mt: 3, mx: 2 }} icon={<AIIcon />}>
            <Typography variant="caption">
              AI zpracování typicky trvá 5-15 sekund. Prosím čekejte...
            </Typography>
          </Alert>
        )}

        <LinearProgress 
          variant="determinate" 
          value={aiProcessing.statusHistory.length > 0 ? aiProcessing.progress : estimatedProgress} 
          sx={{ 
            mt: 3, 
            height: 8, 
            borderRadius: 4,
            mx: 2,
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
            }
          }} 
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {aiProcessing.statusHistory.length > 0 
            ? `${Math.round(aiProcessing.progress)}%` 
            : `Odhadovaný průběh: ${estimatedProgress}%`}
        </Typography>
      </Box>
    );
  };

  // Render review form
  const renderReviewForm = () => {
    if (!editedData) return null;

    return (
      <Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Zkontrolujte extrahovaná data níže. Porovnejte s PDF vlevo a před vytvořením faktury můžete upravit libovolné pole.
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          {/* PDF Preview Column */}
          {pdfPreviewUrl && (
            <Grid item xs={12} md={6} lg={6}>
              <Paper
                elevation={3}
                sx={{
                  position: 'sticky',
                  top: 20,
                  height: '80vh',
                  overflow: 'hidden',
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <PdfIcon />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Originální PDF
                  </Typography>
                </Box>
                <Box sx={{ height: 'calc(100% - 56px)', overflow: 'auto' }}>
                  <iframe
                    src={pdfPreviewUrl}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                    }}
                    title="PDF Preview"
                  />
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Form Column */}
          <Grid item xs={12} md={pdfPreviewUrl ? 6 : 12} lg={pdfPreviewUrl ? 6 : 12}>
            <Grid container spacing={3}>
          {/* Invoice Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Informace o Faktuře
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Číslo Faktury"
              value={editedData.invoiceNumber || ''}
              onChange={(e) => handleFieldChange('invoiceNumber', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Datum Vystavení"
              type="date"
              value={editedData.invoiceDate || ''}
              onChange={(e) => handleFieldChange('invoiceDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Datum Splatnosti"
              type="date"
              value={editedData.dueDate || ''}
              onChange={(e) => handleFieldChange('dueDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Supplier Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Informace o Dodavateli
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Název Dodavatele"
              value={editedData.supplierName || ''}
              onChange={(e) => handleFieldChange('supplierName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Adresa Dodavatele"
              value={editedData.supplierAddress || ''}
              onChange={(e) => handleFieldChange('supplierAddress', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="IČO Dodavatele"
              value={editedData.supplierICO || ''}
              onChange={(e) => handleFieldChange('supplierICO', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="DIČ Dodavatele"
              value={editedData.supplierDIC || ''}
              onChange={(e) => handleFieldChange('supplierDIC', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Bankovní Účet"
              value={editedData.supplierBankAccount || ''}
              onChange={(e) => handleFieldChange('supplierBankAccount', e.target.value)}
            />
          </Grid>

          {/* Customer Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Informace o Odběrateli
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Název Odběratele"
              value={editedData.customerName || ''}
              onChange={(e) => handleFieldChange('customerName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Adresa Odběratele"
              value={editedData.customerAddress || ''}
              onChange={(e) => handleFieldChange('customerAddress', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="IČO Odběratele"
              value={editedData.customerICO || ''}
              onChange={(e) => handleFieldChange('customerICO', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="DIČ Odběratele"
              value={editedData.customerDIC || ''}
              onChange={(e) => handleFieldChange('customerDIC', e.target.value)}
            />
          </Grid>

          {/* Financial Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Finanční Informace
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Základ Daně"
              value={editedData.subtotal || ''}
              onChange={(e) => handleFieldChange('subtotal', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="DPH"
              value={editedData.vatAmount || ''}
              onChange={(e) => handleFieldChange('vatAmount', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Celkem"
              value={editedData.totalAmount || ''}
              onChange={(e) => handleFieldChange('totalAmount', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Měna"
              value={editedData.currency || 'CZK'}
              onChange={(e) => handleFieldChange('currency', e.target.value)}
            />
          </Grid>

          {/* Invoice Items */}
          {editedData.items && editedData.items.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Položky Faktury ({editedData.items.length})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Popis</TableCell>
                      <TableCell align="right">Množství</TableCell>
                      <TableCell align="right">Jedn. Cena</TableCell>
                      <TableCell align="right">Sazba DPH (%)</TableCell>
                      <TableCell align="right">Cena Celkem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editedData.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            value={item.description || ''}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={item.quantity || ''}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={item.unitPrice || ''}
                            onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={item.vatRate || ''}
                            onChange={(e) => handleItemChange(index, 'vatRate', e.target.value)}
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={item.totalPrice || ''}
                            onChange={(e) => handleItemChange(index, 'totalPrice', e.target.value)}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}

          {/* Suggested Business Unit */}
          {editedData.suggestedBusinessUnitName && (
            <Grid item xs={12}>
              <Alert severity="success" icon={<CheckIcon />}>
                <Typography variant="body2">
                  <strong>Navržená Účetní Jednotka:</strong> {editedData.suggestedBusinessUnitName}
                </Typography>
              </Alert>
            </Grid>
          )}

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Poznámky"
              value={editedData.notes || ''}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
            />
          </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Render creating status
  const renderCreatingStatus = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <CircularProgress size={60} sx={{ mb: 2 }} />
      <Typography variant="h6">Vytváření Faktury...</Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AIIcon sx={{ mr: 1 }} />
            Nahrání Faktury s AI
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {aiProcessing.error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {aiProcessing.error}
          </Alert>
        )}

        {activeStep === 0 && renderUploadArea()}
        {activeStep === 1 && renderProcessingStatus()}
        {activeStep === 2 && renderReviewForm()}
        {activeStep === 3 && renderCreatingStatus()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={aiProcessing.isProcessing || aiProcessing.isCreating}>
          Zrušit
        </Button>
        {activeStep === 2 && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateInvoice}
            disabled={!editedData || aiProcessing.isCreating}
            startIcon={<CheckIcon />}
          >
            Vytvořit Fakturu
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AIInvoiceUpload;

